export interface BandwidthTypeByCustomerCell {
  activityType: string;
  activityTypeLabel: string;
  customer: string;
  totalMinutes: number;
  /** False when the source engagement is marked non-billable. */
  isBillable?: boolean;
  /** Leave / Public Holiday — visible in Activity view but excluded from other bandwidth totals. */
  reducesCapacity?: boolean;
}

export interface BandwidthGroupUserDayRow {
  /** UTC calendar day (YYYY-MM-DD). */
  workedDate: string;
  totalActivityCount: number;
  timeSpentHours: number;
  avgHrsOnActivity: number | null;
  /** Leave / Public Holiday day — shown in Teams day drill-down, styled red in UI. */
  reducesCapacity?: boolean;
}

export interface BandwidthGroupUserRow {
  userId: string;
  userName: string;
  totalActivityCount: number;
  timeSpentHours: number;
  avgHrsOnActivity: number | null;
  days: BandwidthGroupUserDayRow[];
  /** True when User.status is inactive or User.active is false (export + KPI filtering). */
  isInactiveUser?: boolean;
}

export interface BandwidthCountsByCustomerRow {
  customerId: string;
  customer: string;
  /** Customer Type label: 'CSP' | 'Non CSP'. */
  customerType?: string | null;
  totalActivityCount: number;
  timeSpentMinutes: number;
  timeSpentHours: number;
  avgHrsOnActivity: number | null;
  users: BandwidthGroupUserRow[];
}

/** Flat customer summary row for Excel/PDF exports (classic Task & Customer counts table). */
export interface BandwidthHeatmapCountsCustomerExportRow {
  customer: string;
  /** Customer Type label: 'CSP' | 'Non CSP'. */
  customerType?: string | null;
  totalActivity: number;
  timeMinutes: number;
  timeHrs: number;
  avgHrsOnActivity: number | null;
  utilizationPct: number | null;
}

export interface BandwidthGroupBreakdownRow {
  groupId: string;
  groupName: string;
  /** Team work location — drives daily capacity (offshore 9h, onshore/onsite 8h). */
  workLocation?: 'offshore' | 'onshore_onsite';
  totalActivityCount: number;
  timeSpentHours: number;
  avgHrsOnActivity: number | null;
  users: BandwidthGroupUserRow[];
}

export interface BandwidthMonthlyTrendPoint {
  monthStart: string;
  monthLabel: string;
  totalHours: number;
  activityCount: number;
}

export interface BandwidthPeriodOverTimePoint {
  periodStart: string;
  periodLabel: string;
  totalHours: number;
  activityCount: number;
}

export interface BandwidthPeriodOverTimeTrend {
  granularity: 'day' | 'month';
  points: BandwidthPeriodOverTimePoint[];
}

export interface BandwidthDayOfWeekPoint {
  dayLabel: string;
  activityCount: number;
}

export interface BandwidthDatesOverview {
  totalActivityCount: number;
  totalTimeSpentHours: number;
  avgHrsOnActivity: number | null;
}

export interface BandwidthActivityTypeCountTimePoint {
  activityType: string;
  activityTypeLabel: string;
  totalActivityCount: number;
  timeSpentHours: number;
}

export interface BandwidthActivityByWeekPoint {
  weekIndex: number;
  weekLabel: string;
  /** UTC Monday YYYY-MM-DD (ISO week start, Mon–Sun; labels match KPI Heatmap Customer week axis). */
  weekStart: string;
  activityCount: number;
}

export interface BandwidthUserCountTimePoint {
  userId: string;
  userName: string;
  totalActivityCount: number;
  timeSpentHours: number;
}

export interface BandwidthTimeByGroupRow {
  /** Present when API returns it; bar/heatmap drill falls back to name → id from filter options. */
  assignmentGroupId?: string;
  assignmentGroup: string;
  totalHours: number;
}

export interface BandwidthTypeByGroupCell {
  /** Present when API returns it; heatmap drill falls back to name → id from filter options. */
  assignmentGroupId?: string;
  activityType: string;
  activityTypeLabel: string;
  assignmentGroup: string;
  totalMinutes: number;
}

export interface BandwidthActivityTypeOption {
  value: string;
  label: string;
}

/** Synthetic team id for rolled-up time; must match backend `bandwidthDashboardService`. */
export const BANDWIDTH_UNALLOCATED_TEAM_ID = '__bandwidth_unallocated__';

/** Minutes logged per team × resource × customer (catalog-resolved group ids). */
export interface BandwidthGroupUserCustomerCell {
  groupId: string;
  userId: string;
  customerId: string;
  minutes: number;
  billableMinutes?: number;
  nonBillableMinutes?: number;
}

/** Active roster members per catalog team (allocation table — no primary-team dedup). */
export interface BandwidthAllocationTeamRosterRow {
  groupId: string;
  users: Array<{ userId: string; userName: string }>;
}

export interface BandwidthAllocationMetrics {
  totalHours: number;
  billableHours: number;
  nonBillableHours: number;
  availableHours: number;
  bandwidthUtilizedPct: number | null;
  bandwidthAvailablePct: number | null;
}

export interface BandwidthTeamResourceAllocationRow extends BandwidthAllocationMetrics {
  id: string;
  label: string;
  kind: 'team' | 'resource';
  resources?: BandwidthTeamResourceAllocationRow[];
}

export interface BandwidthActivityAllocationMetrics {
  totalHours: number;
  billableHours: number;
  nonBillableHours: number;
  billablePct: number | null;
  nonBillablePct: number | null;
}

export interface BandwidthActivityAllocationRow extends BandwidthActivityAllocationMetrics {
  activityType: string;
  activityTypeLabel: string;
}

export interface BandwidthCapacityDeductionCell {
  groupId: string;
  userId: string;
  activityType: string;
  activityTypeLabel: string;
  deductionHours: number;
}

export interface BandwidthDashboardData {
  /** Types that appear in chart data for current duration + customer (independent of type filter). */
  availableActivityTypes: BandwidthActivityTypeOption[];
  typeByCustomer: BandwidthTypeByCustomerCell[];
  countsByCustomer: BandwidthCountsByCustomerRow[];
  timeByAssignmentGroup: BandwidthTimeByGroupRow[];
  typeByAssignmentGroup: BandwidthTypeByGroupCell[];
  groupUserBreakdown: BandwidthGroupBreakdownRow[];
  monthlyTrend: BandwidthMonthlyTrendPoint[];
  /** Dashboard “Time & activity over period”: daily when range ≤ 31 days, else monthly. */
  periodOverTimeTrend: BandwidthPeriodOverTimeTrend;
  activityByDayOfWeek: BandwidthDayOfWeekPoint[];
  datesOverview: BandwidthDatesOverview;
  activityTypeCountVsTime: BandwidthActivityTypeCountTimePoint[];
  /** ISO Mon–Sun UTC weeks; weekStart is each Monday; weekLabel is calendar ISO week (KPI heatmap style). */
  activityByWeek: BandwidthActivityByWeekPoint[];
  userCountVsTime: BandwidthUserCountTimePoint[];
  /** Team × resource × customer minutes for bandwidth allocation tables. */
  groupUserCustomerCells: BandwidthGroupUserCustomerCell[];
  /** Active roster per catalog team for allocation (includes members deduped out of KPI Teams table). */
  allocationTeamRosters: BandwidthAllocationTeamRosterRow[];
  /** Leave / Public Holiday hours per team × user — subtract from capacity totals. */
  capacityDeductionByGroupUser?: BandwidthCapacityDeductionCell[];
  /** Effective UTC date range (YYYY-MM-DD) for this response; use for utilization when duration is “all time”. */
  dashboardRangeFrom: string;
  dashboardRangeTo: string;
  generatedAt: string;
}
